import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  Link,
  Button,
  Flex,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import Logo from '../components/Logo';

const TermsOfService = () => {
  const navigate = useNavigate();

  useEffect(() => {
    // Check for query parameters - URLs with data parameters should not be indexed
    const searchParams = new URLSearchParams(window.location.search);
    const hasDataParam = searchParams.has('data') || searchParams.has('encrypted');
    
    // Set canonical tag for SEO (always use clean URL without query params)
    let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', 'https://abmymix.com/terms');
    
    // Set robots meta tag
    let robotsTag = document.querySelector('meta[name="robots"]') as HTMLMetaElement;
    if (!robotsTag) {
      robotsTag = document.createElement('meta');
      robotsTag.setAttribute('name', 'robots');
      document.head.appendChild(robotsTag);
    }
    
    // If URL has data parameter, set noindex; otherwise allow indexing
    if (hasDataParam) {
      robotsTag.setAttribute('content', 'noindex, nofollow');
    } else {
      robotsTag.setAttribute('content', 'index, follow');
    }
  }, []);

  return (
    <Box minH="100vh" bg="gray.900" color="gray.100">
      <Container maxW="container.lg" py={8}>
        <Flex justify="space-between" align="center" mb={8}>
          <Button
            onClick={() => navigate('/')}
            variant="ghost"
            colorScheme="gray"
            size="sm"
          >
            ← Back to ABmyMix
          </Button>
          <Box
            as="button"
            onClick={() => navigate('/')}
            bg="transparent"
            border="none"
            cursor="pointer"
            _hover={{ opacity: 0.9 }}
          >
            <Box
              w="120px"
              h="40px"
            >
              <Logo />
            </Box>
          </Box>
        </Flex>

        <VStack spacing={6} align="stretch">
          <Heading as="h1" size="xl" textAlign="center" color="gray.100">
            Terms of Service
          </Heading>
          
          <Text fontSize="sm" color="gray.400" textAlign="center">
            Last updated: January 2025
          </Text>

          <VStack spacing={6} align="stretch" mt={8}>
            <Box>
              <Heading as="h2" size="lg" mb={4} color="gray.200">
                Acceptance of Terms
              </Heading>
              <Text>
                By accessing and using ABmyMix, you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.
              </Text>
            </Box>

            <Box>
              <Heading as="h2" size="lg" mb={4} color="gray.200">
                Service Description
              </Heading>
              <Text mb={3}>
                ABmyMix is a web-based audio comparison tool that allows users to:
              </Text>
              <VStack align="stretch" spacing={2} pl={4}>
                <Text>• Compare multiple audio files side by side</Text>
                <Text>• Process audio files locally in their browser</Text>
                <Text>• Share audio comparison setups via Dropbox integration</Text>
                <Text>• Access shared setups created by others</Text>
              </VStack>
            </Box>

            <Box>
              <Heading as="h2" size="lg" mb={4} color="gray.200">
                Acceptable Use
              </Heading>
              <Text mb={3}>
                You agree to use ABmyMix only for lawful purposes and in accordance with these Terms. You agree not to:
              </Text>
              <VStack align="stretch" spacing={2} pl={4}>
                <Text>• Upload or share copyrighted material without proper authorization</Text>
                <Text>• Use the service to distribute malicious or harmful content</Text>
                <Text>• Attempt to reverse engineer, decompile, or extract the source code</Text>
                <Text>• Use the service in any way that could damage, disable, or impair the service</Text>
                <Text>• Share access credentials or allow unauthorized access to your accounts</Text>
                <Text>• Use automated systems to access the service without permission</Text>
              </VStack>
            </Box>

            <Box>
              <Heading as="h2" size="lg" mb={4} color="gray.200">
                Intellectual Property Rights
              </Heading>
              <Text mb={3}>
                <strong>ABmyMix Service:</strong>
              </Text>
              <VStack align="stretch" spacing={2} pl={4} mb={4}>
                <Text>• ABmyMix and its original content, features, and functionality are owned by Neil Thomas</Text>
                <Text>• The service is protected by copyright, trademark, and other intellectual property laws</Text>
                <Text>• You may not reproduce, distribute, or create derivative works without express permission</Text>
              </VStack>
              
              <Text mb={3}>
                <strong>Your Content:</strong>
              </Text>
              <VStack align="stretch" spacing={2} pl={4}>
                <Text>• You retain all rights to audio files you upload or share through the service</Text>
                <Text>• You are responsible for ensuring you have the right to use and share your content</Text>
                <Text>• You grant ABmyMix a limited license to process your files for service functionality</Text>
                <Text>• This license terminates when you stop using the service or remove your content</Text>
              </VStack>
            </Box>

            <Box>
              <Heading as="h2" size="lg" mb={4} color="gray.200">
                Dropbox Integration
              </Heading>
              <Text mb={3}>
                When using Dropbox integration features:
              </Text>
              <VStack align="stretch" spacing={2} pl={4}>
                <Text>• You must comply with Dropbox&apos;s Terms of Service and Acceptable Use Policy</Text>
                <Text>• You are responsible for managing access to your Dropbox files</Text>
                <Text>• ABmyMix is not responsible for Dropbox service availability or data loss</Text>
                <Text>• You can revoke ABmyMix&apos;s access to Dropbox at any time through your Dropbox settings</Text>
              </VStack>
              <Text mt={3}>
                Please review <Link href="https://www.dropbox.com/terms" color="blue.400" isExternal>Dropbox&apos;s Terms of Service</Link> for additional requirements.
              </Text>
            </Box>

            <Box>
              <Heading as="h2" size="lg" mb={4} color="gray.200">
                User Responsibilities
              </Heading>
              <VStack align="stretch" spacing={2} pl={4}>
                <Text>• Ensure you have the legal right to use and share all audio content</Text>
                <Text>• Respect copyright and intellectual property rights of others</Text>
                <Text>• Use strong, unique passwords for your accounts</Text>
                <Text>• Keep your browser and security software up to date</Text>
                <Text>• Report any security vulnerabilities or abuse to us promptly</Text>
                <Text>• Only share setup links with trusted individuals</Text>
              </VStack>
            </Box>

            <Box>
              <Heading as="h2" size="lg" mb={4} color="gray.200">
                Service Availability
              </Heading>
              <Text mb={3}>
                <strong>Service Provision:</strong>
              </Text>
              <VStack align="stretch" spacing={2} pl={4} mb={4}>
                <Text>• ABmyMix is provided &quot;as is&quot; without warranties of any kind</Text>
                <Text>• We strive for high availability but cannot guarantee uninterrupted service</Text>
                <Text>• We may modify, suspend, or discontinue the service at any time</Text>
                <Text>• We will provide reasonable notice for planned maintenance or changes</Text>
              </VStack>
              
              <Text mb={3}>
                <strong>Limitations:</strong>
              </Text>
              <VStack align="stretch" spacing={2} pl={4}>
                <Text>• Service performance depends on your device capabilities and internet connection</Text>
                <Text>• Large audio files may impact performance or cause timeouts</Text>
                <Text>• Some features may not work on older browsers or devices</Text>
              </VStack>
            </Box>

            <Box>
              <Heading as="h2" size="lg" mb={4} color="gray.200">
                Limitation of Liability
              </Heading>
              <Text>
                To the maximum extent permitted by law, ABmyMix and its creator shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, or other intangible losses resulting from your use of the service.
              </Text>
            </Box>

            <Box>
              <Heading as="h2" size="lg" mb={4} color="gray.200">
                Indemnification
              </Heading>
              <Text>
                You agree to defend, indemnify, and hold harmless ABmyMix and its creator from and against any claims, damages, obligations, losses, liabilities, costs, or debt arising from your use of the service or violation of these Terms.
              </Text>
            </Box>

            <Box>
              <Heading as="h2" size="lg" mb={4} color="gray.200">
                Termination
              </Heading>
              <Text mb={3}>
                We may terminate or suspend your access to the service immediately, without prior notice, for any reason, including:
              </Text>
              <VStack align="stretch" spacing={2} pl={4}>
                <Text>• Violation of these Terms of Service</Text>
                <Text>• Fraudulent, abusive, or illegal activity</Text>
                <Text>• Extended periods of inactivity</Text>
              </VStack>
            </Box>

            <Box>
              <Heading as="h2" size="lg" mb={4} color="gray.200">
                Changes to Terms
              </Heading>
              <Text>
                We reserve the right to modify these Terms at any time. We will notify users of material changes by updating the &quot;Last updated&quot; date. Your continued use of the service after changes constitutes acceptance of the new Terms.
              </Text>
            </Box>

            <Box>
              <Heading as="h2" size="lg" mb={4} color="gray.200">
                Governing Law
              </Heading>
              <Text>
                These Terms shall be governed by and construed in accordance with applicable laws, without regard to conflict of law provisions.
              </Text>
            </Box>

            <Box>
              <Heading as="h2" size="lg" mb={4} color="gray.200">
                Contact Information
              </Heading>
              <Text>
                If you have any questions about these Terms of Service, please contact us through the feedback feature in the application.
              </Text>
            </Box>
          </VStack>
        </VStack>
      </Container>
    </Box>
  );
};

export default TermsOfService; 